import React, { useEffect, useState } from "react";
import { getDirectusClient } from "../../lib/directus";
import { Button, Card, Form, Modal, message } from "antd";
import { readItems, createItem, updateItem, deleteItem, aggregate } from "@directus/sdk";
import MenuTable from "./components/MenuTable";
import MenuForm from "./components/MenuForm";
import Layouts from "../../components/Layouts";
import { useSelector } from "react-redux";
import validarpermiso from '../../lib/utils';

function MenuCrud() {
  const [menus, setMenus] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentMenu, setCurrentMenu] = useState(null);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [espadre, setEspadre] = useState(false);
  const [childrenOptions, setChildrenOptions] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 25,
    total: 0,
  });
  const [icons, setIcons] = useState(null);
  const [hasPermissionError, setHasPermissionError] = useState(false);
  const permisos = useSelector((state) => state.permisos);

  // Obtener la lista de menús paginados
  const fetchMenus = async (page = 1, pageSize = 25) => {
    if (!validarpermiso("menu", "read", permisos)) {
      setHasPermissionError(true);
      return;
    }

    setLoading(true);
    try {
      const directus = await getDirectusClient();

      // Obtener los menús paginados
      const response = await directus.request(
        readItems("menu", {
          fields: ["id", "label", "iconv","slug", "espadre", "children", "date_created"],
          sort: ["-date_created"],
          limit: pageSize,
          page: page,
        })
      );

      // Obtener el total de registros usando la función countDistinct
      const totalResponse = await directus.request(
        aggregate("menu", {
          aggregate: {
            countDistinct: ["*"],
          },
        })
      );

      // Verificar si la respuesta tiene la estructura esperada
      if (totalResponse && totalResponse.length > 0) {
        setPagination({
          ...pagination,
          current: page,
          pageSize,
          total: parseInt(totalResponse[0].countDistinct["*"], 10),
        });
      } else {
        console.error("La respuesta de aggregate no tiene la estructura esperada:", totalResponse);
        message.error("Error al obtener el total de registros");
      }

      setMenus(response);
      setHasPermissionError(false);
    } catch (error) {
      console.error("Error fetching menus:", error);
      if (error.response?.status === 403 || error.message?.includes('permission')) {
        setHasPermissionError(true);
        message.error('No tiene permisos para ver los menús');
      } else {
        message.error("Error al cargar los menús");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Dynamic import of Material Icons
    import("@mui/icons-material").then((importedIcons) => {
      setIcons(importedIcons);
    });
    
    // Initial fetch of menus
    fetchMenus(pagination.current, pagination.pageSize);
  }, []);

  const getChildrenMenus = async (idMenu = null) => {
    try {
      const directus = await getDirectusClient();
      
      // If we're editing a menu, first get its current children
      let currentChildren = [];
      if (idMenu) {
        const currentMenu = await directus.request(
          readItems("menu", {
            fields: ["id", "label", "children", "slug"],
            filter: {
              id: { _eq: idMenu }
            }
          })
        );
        // Ensure children is an array
        currentChildren = Array.isArray(currentMenu[0]?.children) 
          ? currentMenu[0].children 
          : [];
      }
      
      // Get available menus (not parents and including current children)
      const filter = {
        _or: [
          { espadre: { _eq: false } }  // Include potential new children
        ],
        id: { _neq: idMenu }           // Exclude current menu
      };

      // Only add _in filter if there are current children
      if (currentChildren.length > 0) {
        filter._or.push({ id: { _in: currentChildren } });
      }

      const response = await directus.request(
        readItems("menu", {
          fields: ["id", "label", "children", "slug"],
          filter: filter,
          sort: ["label"]
        })
      );

      setChildrenOptions(response.map(menu => ({
        id: menu.id,
        label: menu.label,
        isCurrentChild: currentChildren.includes(menu.id)
      })));

    } catch (error) {
      console.error("Error fetching children menus:", error);
      message.error("Error al cargar los menús hijos");
    }
  };

  const showModal = async (menu = null) => {
    try {
      setLoading(true);
      setCurrentMenu(menu);
      
      if (menu) {
        const directus = await getDirectusClient();
        // Fetch current menu with all its data
        const currentMenu = await directus.request(
          readItems("menu", {
            fields: ["id", "label", "iconv", "espadre", "children", "slug"],
            filter: { id: { _eq: menu.id } }
          })
        );
        
        const menuData = currentMenu[0];
        const isParent = menuData?.espadre || false;
        setEspadre(isParent);
      
        form.setFieldsValue({
          label: menuData?.label || '',
          iconv: menuData?.iconv || '',
          espadre: isParent,
          slug: menuData.slug ||'',
          children: menuData?.children || []
        });
        
   
        await getChildrenMenus(menu.id);
      } else {

        setEspadre(false);
        form.resetFields();
        await getChildrenMenus();
      }
  
      setIsModalVisible(true);
    } catch (error) {
      console.error("Error preparing modal:", error);
      message.error("Error al cargar los datos del menú");
    } finally {
      setLoading(false);
    }
  };

  // Cerrar modal
  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
    setEspadre(false);
  };

  // Guardar menú (crear o actualizar)
  const handleSave = async () => {
    const permissionType = currentMenu ? "update" : "create";
    if (!validarpermiso("menu", permissionType, permisos)) {
      message.error(`No tiene permisos para ${currentMenu ? 'editar' : 'crear'} menús`);
      return;
    }

    try {
      const values = await form.validateFields();
      setLoading(true);
      const directus = await getDirectusClient();

      if (currentMenu) {
        // Actualizar menú existente
        await directus.request(
          updateItem("menu", currentMenu.id, values)
        );
        message.success("Menú actualizado exitosamente");
      } else {
        // Crear nuevo menú
        await directus.request(
          createItem("menu", values)
        );
        message.success("Menú creado exitosamente");
      }

      setIsModalVisible(false);
      form.resetFields();
      setEspadre(false);
      fetchMenus(pagination.current, pagination.pageSize);
    } catch (error) {
      console.error("Error saving menu:", error);
      message.error("Error al guardar el menú");
    } finally {
      setLoading(false);
    }
  };

  // Eliminar menú
  const handleDelete = async (id) => {
    if (!validarpermiso("menu", "delete", permisos)) {
      message.error("No tiene permisos para eliminar menús");
      return;
    }

    try {
      setLoading(true);
      const directus = await getDirectusClient();
      await directus.request(
        deleteItem("menu", id)
      );
      message.success("Menú eliminado exitosamente");
      fetchMenus(pagination.current, pagination.pageSize);
    } catch (error) {
      console.error("Error deleting menu:", error);
      message.error("Error al eliminar el menú");
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (pagination) => {
    fetchMenus(pagination.current, pagination.pageSize);
  };

  return (
    <Layouts>
      {!hasPermissionError ? (
        <Card
          title="Gestión de Menús"
          extra={
            validarpermiso("menu", "create", permisos) && (
              <Button 
                type="primary" 
                onClick={() => showModal()}
                disabled={hasPermissionError}
              >
                Crear Menú
              </Button>
            )
          }
        >
          <MenuTable
            menus={menus}
            loading={loading}
            pagination={pagination}
            handleTableChange={handleTableChange}
            showModal={showModal}
            handleDelete={handleDelete}
            permisos={permisos}
          />
        </Card>
      ) : (
        <h3>No tiene permisos para ver menús</h3>
      )}

      <Modal
        title={currentMenu ? "Editar Menú" : "Crear Menú"}
        open={isModalVisible && validarpermiso("menu", currentMenu ? "update" : "create", permisos)}
        onOk={handleSave}
        onCancel={handleCancel}
        confirmLoading={loading}
      >
        <MenuForm
          form={form}
          espadre={espadre}
          setEspadre={setEspadre}
          childrenOptions={childrenOptions}
          icons={icons}
        />
      </Modal>
    </Layouts>
  );
}

export default MenuCrud;